import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    RefreshCw, Repeat, UserPlus, Users, Activity,
    CalendarClock, GitBranch, Target,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
    fetchRetention, fetchUserJourney, fetchLeadSources, fetchRetentionDetail,
} from '../api/analyticsApi';
import EventDetailModal from './EventDetailModal';
import EngagementListModal from './EngagementListModal';
import ChurnFeedbackPanel from './ChurnFeedbackPanel';

const StatCard = ({ Icon, label, value, sub, accent, onClick }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={!onClick}
        className={`text-left bg-white rounded-xl border border-gray-200 p-4 w-full transition-all ${
            onClick ? 'hover:shadow-md hover:border-gray-300 cursor-pointer' : 'cursor-default'
        }`}
    >
        <div className="flex items-center gap-2 mb-2">
            <span className="flex items-center justify-center w-8 h-8 rounded-lg"
                  style={{ background: `${accent}1a`, color: accent }}>
                <Icon className="w-4 h-4" />
            </span>
            <span className="text-xs font-medium text-gray-500">{label}</span>
        </div>
        <div className="text-2xl font-bold text-gray-900">{value}</div>
        {sub != null && <div className="text-xs text-gray-500 mt-0.5">{sub}</div>}
    </button>
);

// Horizontal bar list — rows are clickable to drill into the underlying records.
const BarList = ({ items, color = '#2563eb', valueKey = 'count', labelKey = 'label', onItemClick }) => {
    const max = Math.max(1, ...items.map((i) => i[valueKey] || 0));
    if (items.length === 0) {
        return <p className="text-xs text-gray-400 py-4 text-center">No data in this range yet.</p>;
    }
    return (
        <div className="space-y-2.5">
            {items.map((item, idx) => {
                const clickable = !!onItemClick && (item[valueKey] || 0) > 0;
                return (
                    <button
                        type="button"
                        key={item[labelKey] ?? idx}
                        onClick={clickable ? () => onItemClick(item) : undefined}
                        disabled={!clickable}
                        className={`flex items-center gap-3 w-full text-left rounded-lg px-1 py-0.5 ${
                            clickable ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-default'
                        }`}
                    >
                        <span className="text-xs text-gray-600 w-32 flex-shrink-0 truncate" title={item[labelKey]}>
                            {item[labelKey]}
                        </span>
                        <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
                            <div className="h-4 rounded-full"
                                 style={{ width: `${Math.max(6, ((item[valueKey] || 0) / max) * 100)}%`, background: color }} />
                        </div>
                        <span className="text-xs font-semibold text-gray-900 w-16 text-right">
                            {(item[valueKey] || 0).toLocaleString()}
                            {item.percentage != null && (
                                <span className="text-gray-400 font-normal"> · {item.percentage}%</span>
                            )}
                        </span>
                    </button>
                );
            })}
        </div>
    );
};

// Six-stage funnel where every stage is clickable (drills into its events).
const FunnelBars = ({ funnel = [], onStageClick }) => {
    if (funnel.length === 0) {
        return <p className="text-xs text-gray-400 py-4 text-center">No journey data in this range yet.</p>;
    }
    const max = Math.max(1, ...funnel.map((s) => s.count || 0));
    return (
        <div className="space-y-2">
            {funnel.map((stage) => {
                const clickable = !!onStageClick && (stage.count || 0) > 0;
                return (
                    <button
                        type="button"
                        key={stage.stage}
                        onClick={clickable ? () => onStageClick(stage.stage) : undefined}
                        disabled={!clickable}
                        className={`flex items-center gap-3 w-full text-left rounded-lg px-2 py-1.5 ${
                            clickable ? 'hover:bg-blue-50 cursor-pointer' : 'cursor-default'
                        }`}
                    >
                        <span className="text-xs font-medium text-gray-700 w-24 flex-shrink-0">{stage.stage}</span>
                        <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                            <div className="h-6 rounded-full flex items-center justify-end pr-2 text-[11px] font-bold text-white"
                                 style={{ width: `${Math.max(8, ((stage.count || 0) / max) * 100)}%`, background: '#55C5D0' }}>
                                {(stage.count || 0).toLocaleString()}
                            </div>
                        </div>
                        <span className="text-xs text-gray-500 w-20 text-right flex-shrink-0">
                            {stage.conversion_rate != null ? `${stage.conversion_rate}%` : '—'}
                        </span>
                    </button>
                );
            })}
        </div>
    );
};

const Card = ({ title, Icon, iconColor, children, subtitle }) => (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2">
            <Icon className="w-4 h-4" style={{ color: iconColor }} /> {title}
        </h3>
        {subtitle && <p className="text-xs text-gray-400 mb-4">{subtitle}</p>}
        <div className={subtitle ? '' : 'mt-4'}>{children}</div>
    </div>
);

// Maps a funnel stage label → the BuyerEvent name(s) the EventDetailModal lists.
const STAGE_EVENTS = {
    Signup: 'signup_completed',
    Viewed: 'property_viewed,property_view',
    Saved: 'property_saved',
    Calculator: 'investment_calculator_opened,investment_calculator_calculated,mortgage_calculator_opened,mortgage_calculator_calculated,mortgage_calculator_broker_clicked',
    GeeVee: 'chatbot_session_started',
    Enquiry: 'enquiry_sent',
};

const EngagementPanel = ({ startDate, endDate }) => {
    const [retention, setRetention] = useState(null);
    const [journey, setJourney] = useState(null);
    const [leadSources, setLeadSources] = useState(null);
    const [loading, setLoading] = useState(true);
    const loadTokenRef = useRef(0);

    // Event-based drill-down (funnel stages + lead source) → reuse EventDetailModal.
    const [eventModal, setEventModal] = useState({ open: false, eventName: null, source: null, title: '' });
    // List drill-down (retention buckets + GeeVee questions) → EngagementListModal.
    const [listModal, setListModal] = useState({ open: false, loading: false, title: '', subtitle: '', rows: [], variant: 'buyers' });

    useEffect(() => {
        const myToken = ++loadTokenRef.current;
        setLoading(true);
        Promise.allSettled([
            fetchRetention(startDate, endDate),
            fetchUserJourney(startDate, endDate),
            fetchLeadSources(startDate, endDate),
        ]).then(([r, j, l]) => {
            if (myToken !== loadTokenRef.current) return;
            if (r.status === 'fulfilled') setRetention(r.value); else console.error(r.reason);
            if (j.status === 'fulfilled') setJourney(j.value); else console.error(j.reason);
            if (l.status === 'fulfilled') setLeadSources(l.value); else console.error(l.reason);
            if ([r, j, l].some((p) => p.status === 'rejected')) {
                toast.error('Some engagement metrics failed to load');
            }
        }).finally(() => { if (myToken === loadTokenRef.current) setLoading(false); });
    }, [startDate, endDate]);

    // Open the generic list modal and fill it from a fetcher.
    const openList = useCallback(async (title, subtitle, variant, fetcher) => {
        setListModal({ open: true, loading: true, title, subtitle, rows: [], variant });
        try {
            const data = await fetcher();
            setListModal((m) => ({ ...m, loading: false, rows: data?.rows || [] }));
        } catch (err) {
            console.error('Drill-down failed:', err);
            toast.error('Failed to load list');
            setListModal((m) => ({ ...m, loading: false, rows: [] }));
        }
    }, []);

    const openEvent = (eventName, source, title) =>
        setEventModal({ open: true, eventName, source: source || null, title });

    if (loading && !retention && !journey) {
        return (
            <div className="flex items-center justify-center py-20 text-gray-500">
                <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading engagement metrics…
            </div>
        );
    }

    const signInDist = (retention?.sign_in_distribution || []).map((d) => ({
        label: d.bucket === '1' ? '1 visit'
            : d.bucket === '10+' ? '10+ visits'
            : `${d.bucket} visits`,
        count: d.users,
        bucket: d.bucket,
    }));
    const daysSinceLastVisit = (retention?.days_since_last_visit || []).map((d) => ({
        label: d.bucket,
        count: d.users,
        bucket: d.bucket,
    }));

    return (
        <div className="space-y-6">
            {/* Returning users headline — cards drill into the buyer list */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <StatCard Icon={Repeat} label="Returning Users"
                          value={`${retention?.returning_pct ?? 0}%`}
                          sub={`${retention?.returning_users ?? 0} of ${retention?.active_users ?? 0} active`}
                          accent="#0e9f6e"
                          onClick={() => openList('Returning Users', 'Seen before this period',
                              'buyers', () => fetchRetentionDetail(startDate, endDate, 'users', { segment: 'returning' }))} />
                <StatCard Icon={UserPlus} label="New Users"
                          value={retention?.new_users ?? 0}
                          sub="First seen in range" accent="#2563eb"
                          onClick={() => openList('New Users', 'First seen in this period',
                              'buyers', () => fetchRetentionDetail(startDate, endDate, 'users', { segment: 'new' }))} />
                <StatCard Icon={Users} label="Active Users"
                          value={retention?.active_users ?? 0}
                          sub="Signed in this range" accent="#7c3aed"
                          onClick={() => openList('Active Users', 'Any session in this period',
                              'buyers', () => fetchRetentionDetail(startDate, endDate, 'users', { segment: 'active' }))} />
                <StatCard Icon={Activity} label="Avg Visits / User"
                          value={retention?.avg_visits_per_user ?? 0}
                          sub="Sessions per active user" accent="#d97706" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card title="How many times users sign in" Icon={Repeat} iconColor="#0e9f6e"
                      subtitle="Distribution of session count per active user · click a bar for the list">
                    <BarList items={signInDist} color="#0e9f6e"
                             onItemClick={(item) => openList(
                                 `Users with ${item.label}`, 'By session count in this period',
                                 'buyers', () => fetchRetentionDetail(startDate, endDate, 'signins', { bucket: item.bucket }))} />
                </Card>
                <Card title="Days since last visit" Icon={CalendarClock} iconColor="#d97706"
                      subtitle="How long each active user has been away (one count per user) · click a bar for the list">
                    <BarList items={daysSinceLastVisit} color="#d97706"
                             onItemClick={(item) => openList(
                                 `Away for ${item.label}`, 'Time since each user’s last visit',
                                 'buyers', () => fetchRetentionDetail(startDate, endDate, 'days_since', { bucket: item.bucket }))} />
                </Card>
            </div>

            {/* User journey funnel — each stage drills into its events */}
            <Card title="User Journey" Icon={GitBranch} iconColor="#2563eb"
                  subtitle="Signup → Viewed → Saved → Calculator → GeeVee → Enquiry · click a stage for the list">
                <FunnelBars funnel={journey?.funnel}
                            onStageClick={(stageName) => {
                                const ev = STAGE_EVENTS[stageName];
                                if (ev) openEvent(ev, null, stageName);
                            }} />
            </Card>

            {/* Lead source attribution — click a channel for its enquiries */}
            <Card title="Lead Source Attribution" Icon={Target} iconColor="#7c3aed"
                  subtitle={`Where enquiries came from · ${leadSources?.total ?? 0} total · click a source`}>
                <BarList items={leadSources?.by_source || []} color="#7c3aed"
                         onItemClick={(item) => {
                             if (item.source === 'email') {
                                 openEvent('email_clicked', null, 'Email leads');
                             } else if (item.source === 'calculator') {
                                 openEvent('mortgage_calculator_broker_clicked', null, 'Calculator leads');
                             } else if (item.source === 'geevee') {
                                 openEvent('chatbot_session_started', null, 'GeeVee sessions');
                             } else {
                                 openEvent('enquiry_sent', item.source, `${item.label} enquiries`);
                             }
                         }} />
            </Card>

            {/* Why buyers leave — churn feedback reasons (deactivate / unsubscribe) */}
            <ChurnFeedbackPanel startDate={startDate} endDate={endDate} />

            {/* Drill-down modals */}
            <EventDetailModal
                isOpen={eventModal.open}
                onClose={() => setEventModal((m) => ({ ...m, open: false }))}
                eventName={eventModal.eventName}
                source={eventModal.source}
                title={eventModal.title}
                startDate={startDate}
                endDate={endDate}
            />
            <EngagementListModal
                isOpen={listModal.open}
                onClose={() => setListModal((m) => ({ ...m, open: false }))}
                title={listModal.title}
                subtitle={listModal.subtitle}
                loading={listModal.loading}
                rows={listModal.rows}
                variant={listModal.variant}
            />
        </div>
    );
};

export default EngagementPanel;
